import type { LeadContact, LeadContactType } from '@/types';

import React from 'react';
import { Card, CardBody, Chip, Button, Tooltip } from '@heroui/react';
import {
  RiDeleteBinLine,
  RiMailLine,
  RiPhoneLine,
  RiLinkedinBoxLine,
  RiFacebookCircleLine,
  RiTwitterLine,
  RiInstagramLine
} from '@remixicon/react';

import { DateTimeDisplay } from '@/components/shared/DateTimeDisplay';
import { PermissionGuard } from '@/components/shared/PermissionGuard';

interface LeadContactItemProps {
  contact: LeadContact;
  onDelete?: (contactId: number) => void;
  isDeleting?: boolean;
}

const CONTACT_TYPE_CONFIG: Record<
  LeadContactType,
  {
    icon: React.ElementType;
    label: string;
    color: 'primary' | 'success' | 'secondary' | 'warning' | 'danger' | 'default';
    bgClass: string;
    textClass: string;
  }
> = {
  email: {
    icon: RiMailLine,
    label: 'Email',
    color: 'primary',
    bgClass: 'bg-primary/10',
    textClass: 'text-primary'
  },
  phone: {
    icon: RiPhoneLine,
    label: 'Phone',
    color: 'success',
    bgClass: 'bg-success/10',
    textClass: 'text-success'
  },
  linkedin: {
    icon: RiLinkedinBoxLine,
    label: 'LinkedIn',
    color: 'secondary',
    bgClass: 'bg-secondary/10',
    textClass: 'text-secondary'
  },
  facebook: {
    icon: RiFacebookCircleLine,
    label: 'Facebook',
    color: 'primary',
    bgClass: 'bg-primary/10',
    textClass: 'text-primary'
  },
  twitter: {
    icon: RiTwitterLine,
    label: 'Twitter',
    color: 'primary',
    bgClass: 'bg-primary/10',
    textClass: 'text-primary'
  },
  instagram: {
    icon: RiInstagramLine,
    label: 'Instagram',
    color: 'secondary',
    bgClass: 'bg-secondary/10',
    textClass: 'text-secondary'
  }
};

export function LeadContactItem({ contact, onDelete, isDeleting }: LeadContactItemProps) {
  const config = CONTACT_TYPE_CONFIG[contact.type] ?? CONTACT_TYPE_CONFIG.email;
  const Icon = config.icon;

  const handleCopyValue = () => {
    navigator.clipboard.writeText(contact.value);
  };

  return (
    <Card className="border-default-200 border shadow-sm">
      <CardBody className="p-3">
        <div className="flex items-start justify-between gap-3">
          <div className="flex flex-1 items-start gap-3">
            <div className={`${config.bgClass} ${config.textClass} rounded-lg p-2`}>
              <Icon className="h-5 w-5" />
            </div>
            <div className="flex flex-1 flex-col gap-1">
              <div className="flex items-center gap-2">
                <Chip size="sm" color={config.color} variant="flat">
                  {config.label}
                </Chip>
                {contact.source && (
                  <span className="text-default-400 text-xs">
                    Source: {contact.source.charAt(0).toUpperCase() + contact.source.slice(1)}
                  </span>
                )}
              </div>
              <Tooltip content="Click to copy">
                <button
                  type="button"
                  onClick={handleCopyValue}
                  className="text-default-900 hover:text-primary text-left text-sm font-medium transition-colors"
                >
                  {contact.value}
                </button>
              </Tooltip>
              {contact.added_by && (
                <span className="text-default-500 text-xs">
                  Added by {contact.added_by.first_name} {contact.added_by.last_name}
                  {' • '}
                  <DateTimeDisplay dateString={contact.created_at} />
                </span>
              )}
            </div>
          </div>
          {onDelete && (
            <PermissionGuard permission="LeadContact.destroy">
              <Button
                isIconOnly
                size="sm"
                variant="light"
                color="danger"
                onPress={() => onDelete(contact.id)}
                isLoading={isDeleting}
              >
                <RiDeleteBinLine className="h-4 w-4" />
              </Button>
            </PermissionGuard>
          )}
        </div>
      </CardBody>
    </Card>
  );
}

export default LeadContactItem;
